import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { skipRealtimeForHerd } from "@/hooks/useHerdRealtimeUpdates";
import { trackHerdEvent } from "@/utils/herdAnalytics";

interface MembershipContextType {
  joinHerd: (herdId: string, herdData?: any) => Promise<boolean>;
  leaveHerd: (herdId: string) => Promise<boolean>;
  isJoining: (herdId: string) => boolean;
  isLeaving: (herdId: string) => boolean;
  joiningHerdId: string | null;
  leavingHerdId: string | null;
}

const MembershipContext = createContext<MembershipContextType | null>(null);

export function MembershipProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();
  const [joiningHerdId, setJoiningHerdId] = useState<string | null>(null);
  const [leavingHerdId, setLeavingHerdId] = useState<string | null>(null);

  const joinHerd = useCallback(async (herdId: string, herdData?: any) => {
    if (!user?.id) {
      console.log("[MembershipContext] No user ID, cannot join");
      return false;
    }

    console.log("[MembershipContext] === JOIN HERD START ===");
    console.log("[MembershipContext] Herd ID:", herdId);
    console.log("[MembershipContext] User ID:", user.id);
    
    setJoiningHerdId(herdId);
    
    // Mark this herd as recently modified to skip realtime updates
    skipRealtimeForHerd(herdId);

    // Cancel any outgoing refetches to prevent race conditions
    await queryClient.cancelQueries({ queryKey: ["user-herds", user.id] });
    await queryClient.cancelQueries({ queryKey: ["is-herd-member", herdId] });
    await queryClient.cancelQueries({ queryKey: ["all-herds"] });

    // Store current state for potential rollback
    const currentUserHerds = queryClient.getQueryData(["user-herds", user.id]) as any[] || [];
    console.log("[MembershipContext] Current user herds count:", currentUserHerds.length);
    
    // Optimistic updates
    if (herdData) {
      queryClient.setQueryData(["user-herds", user.id], [...currentUserHerds, herdData]);
    }
    queryClient.setQueryData(["is-herd-member", herdId, user.id], true);
    queryClient.setQueryData(["all-herds"], (old: any[] | undefined) => {
      if (old) {
        return old.map((herd) =>
          herd.id === herdId
            ? { ...herd, member_count: (herd.member_count || 0) + 1 }
            : herd
        );
      }
      return old;
    });
    queryClient.setQueryData(["herd", herdId], (old: any) => {
      if (old) {
        return { ...old, member_count: (old.member_count || 0) + 1 };
      }
      return old;
    });

    try {
      // Check current auth state
      const { data: sessionData } = await supabase.auth.getSession();
      console.log("[MembershipContext] Auth session valid:", !!sessionData?.session);

      console.log("[MembershipContext] Inserting membership...");
      const { data: insertData, error: insertError } = await supabase
        .from("herd_members")
        .insert({
          herd_id: herdId,
          user_id: user.id,
          role: "member",
        })
        .select();

      console.log("[MembershipContext] Insert result:", { insertData, insertError });

      if (insertError) {
        // Handle duplicate key error gracefully - user is already a member
        if (insertError.code === '23505') {
          console.log("[MembershipContext] Duplicate key - user is already a member");
          
          // Don't revert optimistic updates - user IS a member
          // Ensure cache reflects membership state
          queryClient.setQueryData(["is-herd-member", herdId, user.id], true);
          if (herdData && !currentUserHerds.some((h: any) => h.id === herdId)) {
            queryClient.setQueryData(["user-herds", user.id], [...currentUserHerds, herdData]);
          }
          
          toast({ title: "Already a member", description: "You're already in this herd!" });
          
          // Force invalidate to sync cache with database
          queryClient.invalidateQueries({ queryKey: ["user-herds", user.id] });
          queryClient.invalidateQueries({ queryKey: ["herd-members", herdId] });
          
          setJoiningHerdId(null);
          return true;
        }
        throw insertError;
      }

      console.log("[MembershipContext] === JOIN HERD SUCCESS ===");
      
      // Track successful join
      trackHerdEvent({
        event: 'herd_joined',
        userId: user.id,
        herdId,
        herdName: herdData?.name,
        herdCategory: herdData?.category,
      });
      
      toast({ title: "Joined herd!", description: "Welcome to the community" });
      
      // Invalidate queries to sync with database
      queryClient.invalidateQueries({ queryKey: ["user-herds", user.id] });
      queryClient.invalidateQueries({ queryKey: ["herd-members", herdId] });
      
      setJoiningHerdId(null);
      return true;
    } catch (error: any) {
      console.error("[MembershipContext] === JOIN HERD ERROR ===");
      console.error("[MembershipContext] Error details:", error);
      
      // Track failed join
      trackHerdEvent({
        event: 'herd_join_failed',
        userId: user.id,
        herdId,
        herdName: herdData?.name,
        herdCategory: herdData?.category,
        errorMessage: error?.message,
      });
      
      // Revert optimistic updates on error
      queryClient.setQueryData(["user-herds", user.id], currentUserHerds);
      queryClient.setQueryData(["is-herd-member", herdId, user.id], false);
      queryClient.setQueryData(["all-herds"], (old: any[] | undefined) => {
        if (old) {
          return old.map((herd) =>
            herd.id === herdId
              ? { ...herd, member_count: Math.max((herd.member_count || 1) - 1, 0) }
              : herd
          );
        }
        return old;
      });
      queryClient.setQueryData(["herd", herdId], (old: any) => {
        if (old) {
          return { ...old, member_count: Math.max((old.member_count || 1) - 1, 0) };
        }
        return old;
      });
      
      toast({
        title: "Error",
        description: error?.message || "Failed to join herd",
        variant: "destructive",
      });
      setJoiningHerdId(null);
      return false;
    }
  }, [user?.id, queryClient, toast]);

  const leaveHerd = useCallback(async (herdId: string) => {
    if (!user?.id) {
      console.log("[MembershipContext] No user ID, cannot leave");
      return false;
    }

    console.log("[MembershipContext] === LEAVE HERD START ===");
    console.log("[MembershipContext] Herd ID:", herdId);
    console.log("[MembershipContext] User ID:", user.id);
    
    setLeavingHerdId(herdId);
    
    // Mark this herd as recently modified to skip realtime updates
    skipRealtimeForHerd(herdId);
    
    // Cancel any outgoing refetches to prevent race conditions
    await queryClient.cancelQueries({ queryKey: ["user-herds", user.id] });
    await queryClient.cancelQueries({ queryKey: ["is-herd-member", herdId] });
    await queryClient.cancelQueries({ queryKey: ["all-herds"] });
    
    // Store current state for potential rollback
    const currentUserHerds = queryClient.getQueryData(["user-herds", user.id]) as any[] || [];
    const leavingHerdData = currentUserHerds.find((h: any) => h.id === herdId);
    console.log("[MembershipContext] Current user herds count:", currentUserHerds.length);
    console.log("[MembershipContext] Leaving herd data:", leavingHerdData?.name);
    
    // Optimistic updates
    queryClient.setQueryData(
      ["user-herds", user.id], 
      currentUserHerds.filter((h: any) => h.id !== herdId)
    );
    queryClient.setQueryData(["is-herd-member", herdId, user.id], false);
    queryClient.setQueryData(["all-herds"], (old: any[] | undefined) => {
      if (old) {
        return old.map((herd) =>
          herd.id === herdId
            ? { ...herd, member_count: Math.max((herd.member_count || 1) - 1, 0) }
            : herd
        );
      }
      return old;
    });
    queryClient.setQueryData(["herd", herdId], (old: any) => {
      if (old) {
        return { ...old, member_count: Math.max((old.member_count || 1) - 1, 0) };
      }
      return old;
    });

    try {
      // Check current auth state
      const { data: sessionData } = await supabase.auth.getSession();
      console.log("[MembershipContext] Auth session valid:", !!sessionData?.session);
      
      // Retry logic - attempt up to 3 times
      let retries = 3;
      let deleteSuccessful = false;
      
      while (retries > 0 && !deleteSuccessful) {
        const attemptNumber = 4 - retries;
        console.log("[MembershipContext] Delete attempt:", attemptNumber);
        
        const { error, count } = await supabase
          .from("herd_members")
          .delete()
          .eq("herd_id", herdId)
          .eq("user_id", user.id);

        console.log("[MembershipContext] Delete result:", { error, count });

        if (error) {
          console.error("[MembershipContext] Delete error:", error);
          throw error;
        }

        // RLS failure detection - if no rows affected and no error, RLS may be blocking
        if (count === 0 && !error) {
          console.warn("[MembershipContext] DELETE affected 0 rows - possible RLS policy issue");
        }

        // Wait briefly for database to propagate
        await new Promise(resolve => setTimeout(resolve, 200));

        // Post-operation verification - ensure record is actually gone
        const { data: stillMember, error: verifyError } = await supabase
          .from("herd_members")
          .select("id")
          .eq("herd_id", herdId)
          .eq("user_id", user.id)
          .maybeSingle();

        console.log("[MembershipContext] Verification:", { stillMember, verifyError });

        if (!stillMember) {
          deleteSuccessful = true;
          console.log("[MembershipContext] Verified: member record removed");
        } else {
          console.warn("[MembershipContext] Verification failed - record still exists");
          console.warn("[MembershipContext] Retries left:", retries - 1);
          retries--;
          if (retries > 0) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }
      }

      if (!deleteSuccessful) {
        console.error("[MembershipContext] === LEAVE HERD FAILED after 3 attempts ===");
        console.error("[MembershipContext] This likely indicates an RLS policy blocking DELETE");
        throw new Error("Failed to leave herd - record still exists after retries (check RLS policies)");
      }

      console.log("[MembershipContext] === LEAVE HERD SUCCESS ===");
      
      // Track successful leave
      trackHerdEvent({
        event: 'herd_left',
        userId: user.id,
        herdId,
        herdName: leavingHerdData?.name,
        herdCategory: leavingHerdData?.category,
      });
      
      toast({ title: "Left herd", description: "You've left this community" });
      
      setLeavingHerdId(null);
      return true;
    } catch (error: any) {
      console.error("[MembershipContext] === LEAVE HERD ERROR ===");
      console.error("[MembershipContext] Error details:", error);
      
      // Track failed leave
      trackHerdEvent({
        event: 'herd_leave_failed',
        userId: user.id,
        herdId,
        herdName: leavingHerdData?.name,
        herdCategory: leavingHerdData?.category,
        errorMessage: error?.message,
      });
      
      // Revert optimistic updates on error
      if (leavingHerdData) {
        queryClient.setQueryData(["user-herds", user.id], currentUserHerds);
      }
      queryClient.setQueryData(["is-herd-member", herdId, user.id], true);
      queryClient.setQueryData(["all-herds"], (old: any[] | undefined) => {
        if (old) {
          return old.map((herd) =>
            herd.id === herdId
              ? { ...herd, member_count: (herd.member_count || 0) + 1 }
              : herd
          );
        }
        return old;
      });
      queryClient.setQueryData(["herd", herdId], (old: any) => {
        if (old) {
          return { ...old, member_count: (old.member_count || 0) + 1 };
        }
        return old;
      });
      
      toast({
        title: "Error",
        description: error?.message || "Failed to leave herd",
        variant: "destructive",
      });
      setLeavingHerdId(null);
      return false;
    }
  }, [user?.id, queryClient, toast]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    joinHerd,
    leaveHerd,
    isJoining: (herdId: string) => joiningHerdId === herdId,
    isLeaving: (herdId: string) => leavingHerdId === herdId,
    joiningHerdId,
    leavingHerdId,
  }), [joinHerd, leaveHerd, joiningHerdId, leavingHerdId]);

  return (
    <MembershipContext.Provider value={contextValue}>
      {children}
    </MembershipContext.Provider>
  );
}

export function useMembership() {
  const context = useContext(MembershipContext);
  if (!context) {
    throw new Error("useMembership must be used within a MembershipProvider");
  }
  return context;
}
